import { inject, Injectable, InjectionToken } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { ScriptLoaderService, WINDOW_TOKEN } from '@tdwb/core';

/**
 * This is the InjectionToken that allows for the configuration of the TargetedAdService to be passed from the app module on startup
 *
 * @type {InjectionToken<TargetedAdConfiguration>}
 */
export const TARGETED_AD_CONFIGURATION: InjectionToken<TargetedAdConfiguration> = new InjectionToken<TargetedAdConfiguration>('targetedAdConfiguration');

export interface AdDefinition {
  tagId: number;
  sizes: Array<Array<number>>;
  targetId: string;
  native: Record<string, unknown>;
}

/**
 * This is the interface for the configuration object we'll be receiving from the InjectionToken
 */
export interface TargetedAdConfiguration {
  dcxServiceEnabled: boolean;
  tags: Array<AdDefinition>;
  scriptUrl?: string;
  initDelay?: number;
}

interface TargetedAdWindow {
  apntag: {
    anq: Array<unknown>,
    loadTags: () => unknown,
    setPageOpts: (arg: { member: number }) => unknown,
    defineTag: (arg: AdDefinition) => unknown,
    refresh: (arg: Array<string>) => unknown,
    showTag: (arg: string) => unknown
  }
}

/**
 * This Service is used to load the targeted ad script from the backend
 * and then retrieve a particular targeted ad for a given page when the component requests it
 */
@Injectable({
  providedIn: 'root'
})
export class TargetedAdService {
  private static readonly DEFAULT_INIT_DELAY: number = 3000;
  private static readonly MEMBER_ID: number = 10793;
  private static readonly DEFAULT_SCRIPT_URL: string = '//acdn.adnxs.com/ast/ast.js';
  readonly onLoad: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private initialized: boolean = false;

  private readonly adsShown: Array<string> = [];

  private readonly targetedAdConfiguration = inject(TARGETED_AD_CONFIGURATION);
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly browserWindow: TargetedAdWindow = inject(WINDOW_TOKEN) as unknown as TargetedAdWindow;

  /**
   * Initializer to be called at bootstrap
   */
  init(): void {
    if (this.targetedAdConfiguration?.dcxServiceEnabled) {
      this.bootstrap(this.targetedAdConfiguration);
    }

  }

  /**
   * This is the function that is called when the [Targeted Ad Component]{@link TargetedAdComponent} is initializing
   *
   * @param targetId The id that is used to refer to where the ad will show
   * @returns A Promise
   */
  getTargetedAd(targetId: string): void {
    this.showTargetedAd(targetId);
  }

  /**
   * A simple function used to check if the service has been initialized
   *
   * @returns True if the service has been initialized, false otherwise
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * This function is responsible for calling [Script Loader Service's load method]{@link ScriptLoaderService#load}.
   * It completes the subject if the load is successful or throws an error if it didn't load properly
   *
   * @param adConfig Configuration for the targeted ad service.
   */
  private bootstrap(adConfig: TargetedAdConfiguration): void {
    const url = adConfig.scriptUrl ?? TargetedAdService.DEFAULT_SCRIPT_URL;
    const delay = adConfig.initDelay ?? TargetedAdService.DEFAULT_INIT_DELAY;
    if (url !== undefined) {
      setTimeout((): void => {
        this.scriptLoader.load(url).then((): void => {
          this.setUpTags(adConfig);
          this.initialized = true;
          this.onLoad.next(true);
        }, (): void => {
          this.onLoad.error('error');
        });
      }, delay);
    }
  }

  /**
   * A function that is used to set up the ad tags
   */
  private setUpTags(adConfig: TargetedAdConfiguration): void {
    const window = this.browserWindow;
    const apntag = window.apntag;
    if (apntag) {
      apntag.setPageOpts({
        member: TargetedAdService.MEMBER_ID
      });
      adConfig.tags.forEach((tag: AdDefinition): void => {
        window.apntag.anq.push((): void => {
          window.apntag.defineTag(tag);
        });
      });

      if (adConfig.tags.length > 0) {
        window.apntag.anq.push((): void => {
          window.apntag.loadTags();
        });
      }
    }
  }

  /**
   * A function that is used to make the ad show up. If it has been shown previously, it will refresh it.
   *
   * @param targetId The id that is used to refer to where the ad will show
   */
  private showTargetedAd(targetId: string): void {
    const window = this.browserWindow;
    const apntag = window.apntag;
    if (apntag) {
      if (this.adsShown.indexOf(targetId) === -1) {
        apntag.anq.push((): void => {
          apntag.showTag(targetId);
        });
        this.adsShown.push(targetId);
      } else {
        apntag.refresh([targetId]);
      }
    }
  }
}
